import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ArticuloPicker } from './ArticuloPicker'
import { ClientePicker } from './ClientePicker'
import { DireccionNuevaForm } from './DireccionNuevaForm'
import { useFmtArticulo } from '@/hooks/useFmtArticulo'
import {
  useClienteFijo,
  useDimensionesArticulo,
  useDirecciones,
  useOrdenesServicio,
  usePedidoDetalle,
} from '@/hooks/useOrdenesServicio'
import type {
  ArticuloOpcion,
  PedidoVentaCabeceraForm,
  PedidoVentaDetalle,
  PedidoVentaLineaForm,
  PedidoVentaResumen,
} from '@/types'

/** Empresas cuyos artículos no tienen dimensiones de color ni estilo.
 *
 *  Es una propiedad del CATÁLOGO, no del usuario: un Empresa.All creando un
 *  pedido para AVP tampoco tiene nada que elegir ahí. Por eso mira la empresa del
 *  pedido y no el rol de quien abre el formulario. */
const EMPRESAS_SIN_DIMENSIONES = new Set(["avp"])

interface Props {
  dark: boolean
  empresas: string[]
  /** Si viene, el formulario está en modo edición (se carga el detalle real). */
  pedido?: PedidoVentaResumen | null
  onClose: () => void
  onSaved: (numero: string, advertencias: string[]) => void
}

/** Mensaje legible de un error de la API (detail del backend o message). */
const apiError = (e: unknown): string => {
  const detail = (e as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail
  if (typeof detail === 'string') return detail
  return (e as Error)?.message ?? ''
}

export function PedidoVentaForm({ dark, empresas, pedido, onClose, onSaved }: Props) {
  const { t } = useTranslation()
  const editing = !!pedido
  // En edición se carga el detalle real (líneas con lineId) antes de pintar el form.
  const detalle = usePedidoDetalle(pedido?.numero ?? '', pedido?.empresa, editing)

  if (editing && !detalle.data) {
    return (
      <div className="fixed inset-0 z-40 flex">
        <div className="absolute inset-0 bg-black/40 backdrop-blur-[1px]" onClick={onClose} />
        <div className={`relative ml-auto h-full w-full max-w-2xl border-l shadow-2xl flex items-center justify-center ${
          dark ? 'bg-slate-900 border-white/10' : 'bg-slate-50 border-slate-200'
        }`}>
          <p className={`text-[13px] ${dark ? 'text-slate-400' : 'text-slate-500'}`}>
            {detalle.isError ? apiError(detalle.error) || t('ordenesServicio.form.errorDetalle') : '…'}
          </p>
        </div>
      </div>
    )
  }

  return (
    <PedidoVentaFormInner
      dark={dark}
      empresas={empresas}
      detalle={editing ? detalle.data! : null}
      onClose={onClose}
      onSaved={onSaved}
    />
  )
}

interface InnerProps {
  dark: boolean
  empresas: string[]
  detalle: PedidoVentaDetalle | null
  onClose: () => void
  onSaved: (numero: string, advertencias: string[]) => void
}

function PedidoVentaFormInner({ dark, empresas, detalle, onClose, onSaved }: InnerProps) {
  const { t } = useTranslation()
  const fmtArt = useFmtArticulo()
  const { crear, actualizar } = useOrdenesServicio()
  const editing = !!detalle

  const [cab, setCab] = useState<PedidoVentaCabeceraForm>(() =>
    detalle
      ? {
          empresa: detalle.empresa,
          cliente: detalle.cliente,
          nombreCliente: detalle.nombreCliente ?? '',
          numeroPedidoCliente: detalle.numeroPedidoCliente ?? '',
          fechaEnvio: detalle.fechaEnvio ?? '',
          direccionLocationId: detalle.direccionLocationId ?? '',
          direccionTexto: detalle.direccionTexto ?? '',
        }
      : {
          empresa: empresas[0] ?? '',
          cliente: '',
          nombreCliente: '',
          numeroPedidoCliente: '',
          fechaEnvio: '',
          direccionLocationId: '',
          direccionTexto: '',
        },
  )
  const [lineas, setLineas] = useState<PedidoVentaLineaForm[]>(() =>
    detalle ? detalle.lineas.map((l) => ({ ...l })) : [],
  )
  const [error, setError] = useState<string | null>(null)
  const [nuevaDireccionOpen, setNuevaDireccionOpen] = useState(false)

  // Los roles de cliente externo operan siempre sobre su cuenta principal: se
  // fija y se oculta el buscador. El resto elige libremente.
  const { data: clienteFijo } = useClienteFijo()
  useEffect(() => {
    if (editing || !clienteFijo?.cuenta) return
    setCab((c) => (c.cliente ? c : {
      ...c,
      cliente: clienteFijo.cuenta,
      nombreCliente: clienteFijo.nombre ?? '',
      empresa: clienteFijo.dataareaid || c.empresa,
    }))
  }, [clienteFijo, editing])

  const { data: direcciones = [] } = useDirecciones(cab.cliente, cab.empresa)

  const set = <K extends keyof PedidoVentaCabeceraForm>(k: K, v: PedidoVentaCabeceraForm[K]) =>
    setCab((c) => ({ ...c, [k]: v }))

  const addLinea = (a: ArticuloOpcion) =>
    setLineas((ls) => [
      ...ls,
      { articulo: a.itemid, descripcion: a.descripcion, cantidad: 1, unidad: 'unidades', esArmaf: a.linea === 'ARMAF' },
    ])

  const setLinea = (i: number, patch: Partial<PedidoVentaLineaForm>) =>
    setLineas((ls) => ls.map((l, idx) => (idx === i ? { ...l, ...patch } : l)))

  const removeLinea = (i: number) => setLineas((ls) => ls.filter((_, idx) => idx !== i))

  const busy = crear.isPending || actualizar.isPending

  const guardar = async () => {
    setError(null)
    if (!cab.empresa || !cab.cliente.trim()) { setError(t('ordenesServicio.form.faltaCabecera')); return }
    if (!cab.numeroPedidoCliente?.trim()) { setError(t('ordenesServicio.form.faltaNumeroPedidoCliente')); return }
    if (!cab.fechaEnvio) { setError(t('ordenesServicio.form.faltaFechaEnvio')); return }
    if (!cab.direccionLocationId) { setError(t('ordenesServicio.form.faltaDireccion')); return }
    if (lineas.length === 0) { setError(t('ordenesServicio.form.faltaLinea')); return }
    if (lineas.some((l) => !(l.cantidad > 0))) { setError(t('ordenesServicio.form.cantidadInvalida')); return }
    try {
      const res = editing && detalle
        ? await actualizar.mutateAsync({ numero: detalle.numero, cab, lineas })
        : await crear.mutateAsync({ cab, lineas })
      onSaved(res.pedido.numero, res.advertencias)
    } catch (e) {
      setError(apiError(e) || t('ordenesServicio.form.errorGuardar'))
    }
  }

  // Empresas cuyos artículos no llevan dimensiones: los selectores de color y
  // estilo saldrían siempre vacíos y deshabilitados, y cada línea gastaría una
  // consulta de dimensiones para no enseñar nada.
  const sinDimensiones = EMPRESAS_SIN_DIMENSIONES.has(cab.empresa.trim().toLowerCase())

  // estilos
  const panel = dark ? 'bg-slate-900 border-white/10' : 'bg-slate-50 border-slate-200'
  const card = dark ? 'bg-white/5 border-white/10' : 'bg-white border-slate-200'
  const label = `block text-[11px] font-semibold mb-1 ${dark ? 'text-slate-400' : 'text-slate-500'}`
  const input = `w-full min-h-[38px] px-3 rounded-lg border text-[13px] ${
    dark ? 'bg-white/5 border-white/15 text-slate-100' : 'bg-white border-slate-300 text-slate-800'
  }`
  const readonly = `w-full min-h-[38px] px-3 rounded-lg border text-[13px] ${
    dark ? 'bg-white/[0.02] border-white/10 text-slate-300' : 'bg-slate-100 border-slate-200 text-slate-600'
  }`
  const mainTxt = dark ? 'text-slate-100' : 'text-slate-900'
  const mutedTxt = dark ? 'text-slate-400' : 'text-slate-500'
  const bar = dark ? 'border-white/10 bg-slate-900' : 'border-slate-200 bg-slate-50'
  const btnPrimary = 'px-4 min-h-[40px] rounded-lg text-[13px] font-semibold bg-sky-600 text-white hover:bg-sky-500 disabled:opacity-50 transition-colors shadow-sm'
  const btnGhost = `px-4 min-h-[40px] rounded-lg text-[13px] font-semibold border transition-colors ${
    dark ? 'border-white/15 text-slate-300 hover:bg-white/10' : 'border-slate-300 text-slate-600 hover:bg-slate-100'
  }`

  return (
    <div className="fixed inset-0 z-40 flex">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-[1px]" onClick={onClose} />
      <div className={`relative ml-auto h-full w-full max-w-2xl overflow-y-auto border-l shadow-2xl ${panel}`}>
        {/* Cabecera del panel */}
        <div className={`sticky top-0 z-10 flex items-center justify-between gap-3 px-5 py-4 border-b ${bar}`}>
          <h2 className={`text-[16px] font-black tracking-tight ${mainTxt}`}>
            {editing ? t('ordenesServicio.form.editarTitulo', { numero: detalle!.numero }) : t('ordenesServicio.form.title')}
          </h2>
          <button type="button" onClick={onClose} className={`text-[13px] ${mutedTxt} hover:underline`}>✕</button>
        </div>

        <div className="p-5 space-y-5">
          {/* Bloque cabecera */}
          <section className={`rounded-xl border shadow-sm p-4 ${card}`}>
            <h3 className={`text-[12px] font-bold uppercase tracking-wide mb-3 ${mutedTxt}`}>{t('ordenesServicio.form.cabecera')}</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className={label}>{t('ordenesServicio.form.empresa')}</label>
                <select
                  className={input}
                  value={cab.empresa}
                  disabled={editing || !!clienteFijo}
                  onChange={(e) => setCab((prev) => ({
                    ...prev,
                    empresa: e.target.value,
                    // El cliente pertenece a la empresa anterior: dejarlo puesto
                    // daría una lista de direcciones vacía sin explicación.
                    cliente: '', nombreCliente: '',
                    direccionLocationId: '', direccionTexto: '',
                  }))}
                >
                  {empresas.map((e) => <option key={e} value={e}>{e.toUpperCase()}</option>)}
                </select>
              </div>
              <div>
                <label className={label}>{t('ordenesServicio.form.cliente')} *</label>
                {clienteFijo ? (
                  // Rol de cliente externo: su cuenta es la única sobre la que
                  // puede operar, así que no hay nada que buscar.
                  <input
                    className={readonly}
                    value={cab.nombreCliente || cab.cliente}
                    readOnly
                    placeholder="—"
                  />
                ) : (
                  <ClientePicker
                    dark={dark}
                    value={cab.nombreCliente || cab.cliente}
                    onSelect={(c) => setCab((prev) => ({
                      ...prev,
                      cliente: c.cuenta,
                      nombreCliente: c.nombre,
                      // El cliente manda sobre la empresa: una cuenta pertenece a
                      // una entidad legal, y consultar sus direcciones con otra
                      // devuelve cero. El buscador ofrece clientes de todas las
                      // empresas del usuario, así que hay que sincronizarla.
                      empresa: c.dataareaid || prev.empresa,
                      direccionLocationId: '', direccionTexto: '',
                    }))}
                  />
                )}
              </div>
              <div>
                <label className={label}>{t('ordenesServicio.form.cuentaCliente')}</label>
                <input className={readonly} value={cab.cliente} readOnly placeholder="—" />
              </div>
              <div>
                <label className={label}>{t('ordenesServicio.form.numeroPedidoCliente')} *</label>
                <input
                  className={input}
                  value={cab.numeroPedidoCliente}
                  onChange={(e) => set('numeroPedidoCliente', e.target.value)}
                  placeholder={t('ordenesServicio.form.numeroPedidoClientePh')}
                />
              </div>
              <div className="sm:col-span-2">
                <label className={label}>{t('ordenesServicio.form.direccionEnvio')} *</label>
                <div className="flex items-center gap-2">
                  <select
                    className={`${cab.cliente ? input : readonly} cursor-pointer`}
                    value={cab.direccionLocationId ?? ''}
                    disabled={!cab.cliente}
                    onChange={(e) => {
                      const d = direcciones.find((x) => x.locationId === e.target.value)
                      setCab((prev) => ({
                        ...prev,
                        direccionLocationId: e.target.value,
                        direccionTexto: d ? (d.texto || [d.calle, d.cp, d.ciudad].filter(Boolean).join(', ')) : '',
                      }))
                    }}
                  >
                    <option value="">{cab.cliente ? t('ordenesServicio.form.direccionVacio') : t('ordenesServicio.form.clientePrimero')}</option>
                    {direcciones.map((d) => (
                      <option key={d.locationId} value={d.locationId}>
                        {d.texto || [d.descripcion, d.calle, d.cp, d.ciudad].filter(Boolean).join(', ')}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={() => setNuevaDireccionOpen(true)}
                    disabled={!cab.cliente}
                    title={t('ordenesServicio.direccionNueva.abrir')}
                    className={`shrink-0 min-h-[38px] px-3 rounded-lg border text-[13px] font-semibold whitespace-nowrap transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                      dark
                        ? 'border-white/15 text-sky-400 hover:bg-white/10'
                        : 'border-slate-300 text-sky-600 hover:bg-slate-100'
                    }`}
                  >
                    + {t('ordenesServicio.direccionNueva.abrir')}
                  </button>
                </div>
              </div>
              <div>
                <label className={label}>{t('ordenesServicio.form.fechaEnvio')} *</label>
                <input type="date" className={input} value={cab.fechaEnvio} onChange={(e) => set('fechaEnvio', e.target.value)} />
              </div>
            </div>
          </section>

          {/* Bloque líneas */}
          <section className={`rounded-xl border shadow-sm p-4 ${card}`}>
            <h3 className={`text-[12px] font-bold uppercase tracking-wide mb-3 ${mutedTxt}`}>{t('ordenesServicio.form.lineas')}</h3>
            <ArticuloPicker dark={dark} onSelect={addLinea} />

            {lineas.length === 0 ? (
              <p className={`text-[12px] mt-3 ${mutedTxt}`}>{t('ordenesServicio.form.sinLineas')}</p>
            ) : (
              <ul className="mt-3 space-y-2">
                {lineas.map((l, i) => (
                  <li key={`${l.articulo}-${l.lineId ?? i}`} className={`rounded-lg border p-3 ${dark ? 'border-white/10 bg-white/5' : 'border-slate-200 bg-slate-50'}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <span className={`text-[13px] font-semibold ${mainTxt}`}>{fmtArt(l.articulo)}</span>
                      {l.esArmaf && <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-500">ARMAF</span>}
                      <span className={`flex-1 text-[12px] truncate ${mutedTxt}`}>{l.descripcion}</span>
                      {l.lineId ? (
                        // Las líneas ya guardadas en D365 no se pueden eliminar desde
                        // la app (la cancelación es un proceso interno de Dynamics).
                        <span className={`text-[11px] shrink-0 ${mutedTxt}`} title={t('ordenesServicio.form.lineaFijaHint')}>
                          {t('ordenesServicio.form.lineaFija')}
                        </span>
                      ) : (
                        <button type="button" onClick={() => removeLinea(i)} className="text-[12px] text-red-500 hover:underline shrink-0">
                          {t('ordenesServicio.form.eliminar')}
                        </button>
                      )}
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      <div>
                        <label className={label}>{t('ordenesServicio.form.cantidad')}</label>
                        <div className="flex items-center gap-2">
                          <input type="number" min={0} className={input} value={l.cantidad}
                            onChange={(e) => setLinea(i, { cantidad: Number(e.target.value) })} />
                          <span className={`text-[12px] shrink-0 ${mutedTxt}`}>{t('ordenesServicio.form.unidadFija')}</span>
                        </div>
                      </div>
                      <div>
                        <label className={label}>{t('ordenesServicio.form.precio')}</label>
                        {/* Sólo lectura: el precio lo pone la lista de precios de
                            D365. Se sigue enseñando —hace falta para revisar el
                            pedido— pero no se edita, y el backend tampoco lo
                            escribiría si llegara. */}
                        <input
                          type="number"
                          className={readonly}
                          value={l.precio ?? ''}
                          readOnly
                          tabIndex={-1}
                          aria-readonly="true"
                          title={t('ordenesServicio.form.precioFijoHint')}
                        />
                        <p className={`mt-0.5 text-[10px] ${mutedTxt}`}>
                          {t('ordenesServicio.form.precioFijo')}
                        </p>
                      </div>
                      <div>
                        <label className={label}>{t('ordenesServicio.form.fechaEnvioLinea')}</label>
                        <input type="date" className={input} value={l.fechaEnvio ?? ''}
                          onChange={(e) => setLinea(i, { fechaEnvio: e.target.value || undefined })} />
                      </div>
                      {/* Los artículos de AVP no tienen color ni estilo: los
                          selectores saldrían siempre vacíos y deshabilitados. El
                          criterio es la empresa del PEDIDO y no el rol de quien
                          lo abre, porque la dimensión es del artículo. */}
                      {!sinDimensiones && (
                        <LineaDims
                          dark={dark}
                          empresa={cab.empresa}
                          linea={l}
                          onPatch={(patch) => setLinea(i, patch)}
                          inputCls={input}
                          labelCls={label}
                        />
                      )}
                    </div>
                    {!l.fechaEnvio && cab.fechaEnvio && (
                      <p className={`mt-1 text-[11px] ${mutedTxt}`}>
                        {t('ordenesServicio.form.heredaFecha', { fecha: cab.fechaEnvio })}
                      </p>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </section>

          {error && <p className="text-[12px] text-red-500">{error}</p>}
        </div>

        {/* Footer acciones */}
        <div className={`sticky bottom-0 flex items-center justify-end gap-2 px-5 py-4 border-t ${bar}`}>
          <button type="button" onClick={onClose} className={btnGhost}>{t('ordenesServicio.form.cancelar')}</button>
          <button type="button" onClick={guardar} disabled={busy} className={btnPrimary}>
            {busy ? '…' : editing ? t('ordenesServicio.form.guardarCambios') : t('ordenesServicio.form.guardar')}
          </button>
        </div>
      </div>

      {nuevaDireccionOpen && (
        <DireccionNuevaForm
          dark={dark}
          cuenta={cab.cliente}
          nombreCliente={cab.nombreCliente}
          empresa={cab.empresa}
          onClose={() => setNuevaDireccionOpen(false)}
          onCreated={(d) => {
            setNuevaDireccionOpen(false)
            // Se deja seleccionada: es la que el usuario acaba de dar de alta.
            setCab((prev) => ({
              ...prev,
              direccionLocationId: d.locationId,
              direccionTexto: d.texto || [d.calle, d.cp, d.ciudad].filter(Boolean).join(', '),
            }))
          }}
        />
      )}
    </div>
  )
}

/** Selectores Color/Estilo de una línea, alimentados por las combinaciones
 *  reales del artículo en Fabric. Si el artículo no tiene dims, se deshabilitan. */
function LineaDims({ dark, empresa, linea, onPatch, inputCls, labelCls }: {
  dark: boolean
  empresa: string
  linea: PedidoVentaLineaForm
  onPatch: (patch: Partial<PedidoVentaLineaForm>) => void
  inputCls: string
  labelCls: string
}) {
  const { t } = useTranslation()
  const { data: dims = [], isFetching } = useDimensionesArticulo(linea.articulo, empresa)
  const mutedTxt = dark ? 'text-slate-400' : 'text-slate-500'

  const colores = [...new Set(dims.map((d) => d.color).filter(Boolean))]
  const estilos = [...new Set(
    dims.filter((d) => !linea.color || !d.color || d.color === linea.color)
      .map((d) => d.estilo)
      .filter(Boolean),
  )]

  const pickColor = (color: string) => {
    const validos = dims.filter((d) => !color || !d.color || d.color === color).map((d) => d.estilo)
    onPatch({ color: color || undefined, estilo: linea.estilo && validos.includes(linea.estilo) ? linea.estilo : undefined })
  }

  return (
    <>
      <div>
        <label className={labelCls}>{t('ordenesServicio.form.color')}</label>
        <select
          className={`${inputCls} cursor-pointer disabled:opacity-60`}
          value={linea.color ?? ''}
          disabled={colores.length === 0}
          onChange={(e) => pickColor(e.target.value)}
        >
          <option value="">{isFetching ? '…' : colores.length === 0 ? t('ordenesServicio.form.sinDims') : '—'}</option>
          {colores.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>
      <div>
        <label className={labelCls}>{t('ordenesServicio.form.estilo')}</label>
        <select
          className={`${inputCls} cursor-pointer disabled:opacity-60`}
          value={linea.estilo ?? ''}
          disabled={estilos.length === 0}
          onChange={(e) => onPatch({ estilo: e.target.value || undefined })}
        >
          <option value="">{isFetching ? '…' : estilos.length === 0 ? t('ordenesServicio.form.sinDims') : '—'}</option>
          {estilos.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>
      {isFetching && <span className={`sr-only ${mutedTxt}`}>…</span>}
    </>
  )
}
